import React, { forwardRef, useImperativeHandle, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputGroup from '../../../../components/InputGroup';
import TextAreaGroup from '../../../../components/TextAreaGroup';
import Checkbox from '../../../../components/Checkbox';
import EventParticipantsList from './EventParticipantsList';
import { CreateEventFormData } from './types';
import { SearchableCombobox } from '../../../../components/SearchableCombobox';
// import { useRoomResourceSearch } from '../hooks/useRoomResourceSearch';
import { useParticipantSearch } from '../hooks/useParticipantSearch';
import { getCurrentTimezone } from '../utils/timezoneUtils';
import { TimezoneSelect } from './TimezoneSelect';

export interface EventFormRef {
  submit: () => void;
}

interface EventFormProps {
  defaultValues: CreateEventFormData;
  onSubmit: (data: CreateEventFormData) => void;
  isAllDay: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
  onValidationFailed?: () => void;
  /** Called when form would submit (Enter in input, etc). Parent should run guard + formRef.submit(). */
  onSubmitRequest?: () => void;
}

const EventForm = forwardRef<EventFormRef, EventFormProps>(
  (
    {
      defaultValues,
      onSubmit,
      isAllDay,
      onSubmittingChange,
      onValidationFailed,
      onSubmitRequest,
    },
    ref,
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      control,
      formState: { errors, isSubmitting },
    } = useForm<CreateEventFormData>({
      defaultValues: {
        ...defaultValues,
        when: {
          ...defaultValues.when,
          startTimezone:
            defaultValues.when.startTimezone || getCurrentTimezone(),
          endTimezone: defaultValues.when.endTimezone || getCurrentTimezone(),
        },
      },
    });

    const { searchParticipants } = useParticipantSearch();
    // const { searchRoomResources } = useRoomResourceSearch();

    const isAllDayChecked = watch('isAllDay', isAllDay);
    const participants = watch('participants', defaultValues.participants);

    useImperativeHandle(
      ref,
      () => ({
        submit: () => handleSubmit(onSubmit, () => onValidationFailed?.())(),
      }),
      [handleSubmit, onSubmit, onValidationFailed],
    );

    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    const removeParticipant = (index: number) => {
      const updatedParticipants = participants.filter((_, i) => i !== index);
      setValue('participants', updatedParticipants);
    };

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (onSubmitRequest) {
            onSubmitRequest();
          } else {
            handleSubmit(onSubmit, () => onValidationFailed?.())();
          }
        }}
        className="space-y-4"
      >
        <InputGroup
          label="Title"
          useNaturalLettering
          errorMessage={errors.title?.message}
          required
          inputSize="small"
          {...register('title', { required: 'Title is required' })}
        />
        <Checkbox
          id="isAllDay"
          labelContent="All day"
          className="flex items-center gap-2"
          checkboxClassName="text-green-150 focus:ring-secondary-50"
          {...register('isAllDay')}
        />
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            label="Start Date"
            useNaturalLettering
            type="date"
            errorMessage={errors.when?.startDate?.message}
            required
            inputSize="small"
            {...register('when.startDate', {
              required: 'Start date is required',
            })}
          />
          {isAllDayChecked && (
            <InputGroup
              label="End Date"
              type="date"
              useNaturalLettering
              errorMessage={errors.when?.endDate?.message}
              required
              inputSize="small"
              {...register('when.endDate', {
                required: isAllDayChecked,
                shouldUnregister: true,
              })}
            />
          )}
        </div>
        {!isAllDayChecked && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <InputGroup
                label="Start Time"
                useNaturalLettering
                type="time"
                errorMessage={errors.when?.startTime?.message}
                required
                inputSize="small"
                {...register('when.startTime', {
                  required: !isAllDayChecked,
                  shouldUnregister: true,
                })}
              />
              <InputGroup
                label="End Time"
                useNaturalLettering
                type="time"
                errorMessage={errors.when?.endTime?.message}
                required
                inputSize="small"
                {...register('when.endTime', {
                  required: !isAllDayChecked,
                  shouldUnregister: true,
                })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="when.startTimezone"
                control={control}
                render={({ field }) => (
                  <TimezoneSelect
                    label="Start Timezone"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="when.endTimezone"
                control={control}
                render={({ field }) => (
                  <TimezoneSelect
                    label="End Timezone"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </>
        )}
        <SearchableCombobox
          control={control}
          name="participants"
          label="Participants"
          searchFunction={searchParticipants}
        />
        <EventParticipantsList
          participants={participants}
          onRemoveParticipant={(email, index) => removeParticipant(index)}
        />
        {/* TODO: Bring back this and a list of rooms/buildings */}
        {/* <SearchableCombobox
        control={control}
        name="resources"
        label="Rooms & Buildings"
        searchFunction={searchRoomResources}
      /> */}
        <TextAreaGroup
          label="Description"
          errorMessage={errors.description?.message}
          inputSize="small"
          {...register('description')}
        />
        <InputGroup
          label="Location"
          useNaturalLettering
          errorMessage={errors.location?.message}
          inputSize="small"
          {...register('location')}
        />
      </form>
    );
  },
);

EventForm.displayName = 'EventForm';

export default EventForm;
